/**
 * EventsContext - Shared events data provider
 *
 * This context manages event fetching and shares data between:
 * - EventList (main window UI)
 * - EventsDataProvider (popup communication)
 *
 * Key features:
 * - Single source of truth for events data
 * - Trailing mode support for live updates
 * - Automatic push to popup windows when data changes
 * - Listens for date range changes from popup
 */
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { DateRange } from 'react-day-picker';
import {
  DetectedEvent,
  useGetEvents,
  useGetEventsForPeriod,
} from '@ais-ui/websockets';
import {
  CombinedGroundingEventAndConfiguration,
  useGetGroundingEventsAndConfigurations,
} from './Detectors/Grounding/GroundingEvent';
import {
  CombinedS2SEventAndConfiguration,
  useGetCurrentS2SEventsAndConfigurations,
} from './Detectors/S2S/S2SEvent';
import { useSettings } from '../Settings/SettingsProvider';
import { usePermissions } from '../Permissions/PermissionsProvider';
import {
  MainWindowActions,
  DataType,
  usePopupMessages,
  MessageType,
  PopupMessage,
} from '../Popup/PopupEvents';
import { isPopupTypeActive } from '../Popup/usePopupEventHandler';
import { PopupType } from '@ais-ui/popups';

interface EventsContextValue {
  // Events data
  events: DetectedEvent[];
  groundingEvents: CombinedGroundingEventAndConfiguration[];
  s2sEvents: CombinedS2SEventAndConfiguration[];
  isLoading: boolean;

  // Date range
  date: DateRange | undefined;
  setDate: React.Dispatch<React.SetStateAction<DateRange | undefined>>;

  // Trailing mode
  isTrailing: boolean;
  setIsTrailing: React.Dispatch<React.SetStateAction<boolean>>;

  // Default lookback time from settings
  defaultLookBackTime: number;
}

const EventsContext = createContext<EventsContextValue | null>(null);

const getDefaultDateRange = (lookBackMinutes: number): DateRange => ({
  from: new Date(Date.now() - lookBackMinutes * 60 * 1000),
  to: new Date(),
});

interface EventsProviderProps {
  children: ReactNode;
}

export const EventsProvider: React.FC<EventsProviderProps> = ({ children }) => {
  const { settings } = useSettings();

  const defaultLookBackTime = useMemo(() => {
    const value = Number(
      settings['ribbonPlugin.settings']?.event_lookback_minutes,
    );
    // Cap at 1 year (525600 min) to prevent Date overflow from legacy settings
    const MAX_LOOKBACK = 525600;
    return Number.isFinite(value) && value > 0
      ? Math.min(value, MAX_LOOKBACK)
      : 720;
  }, [settings]);

  const [date, setDate] = useState<DateRange | undefined>(() =>
    getDefaultDateRange(defaultLookBackTime),
  );
  const [isTrailing, setIsTrailing] = useState(true);
  const [events, setEvents] = useState<DetectedEvent[]>([]);
  const [groundingEvents, setGroundingEvents] = useState<
    CombinedGroundingEventAndConfiguration[]
  >([]);
  const [s2sEvents, setS2sEvents] = useState<
    CombinedS2SEventAndConfiguration[]
  >([]);
  const [queryDate, setQueryDate] = useState(date);

  // olserver events are only fetched for users with the events permission,
  // like grounding/S2S below are gated on theirs.
  const { events: hasEventsPermission } = usePermissions();

  // Subscribe to real-time events
  useGetEvents(Boolean(hasEventsPermission));

  // Fetch events for period
  const { data, isLoading, isFetching } = useGetEventsForPeriod(
    queryDate?.from,
    queryDate?.to,
    Boolean(hasEventsPermission),
  );

  // Fetch grounding events
  const { groundingEventsAndConfigurations, groundingIsLoading } =
    useGetGroundingEventsAndConfigurations(queryDate);

  // Fetch S2S events (uses time_range param, same pattern as grounding)
  const { s2sEventsAndConfigurations, s2sIsLoading } =
    useGetCurrentS2SEventsAndConfigurations(queryDate);

  // Advance the trailing window from the always-mounted provider.
  // The in-app EventList panel has its own trailing timer, but it is unmounted
  // when the events popup ("separate page") is open. Owning the timer here
  // guarantees trailing mode keeps updating regardless of which UI is visible,
  // so switching from historical to tailing in the popup refreshes the view.
  useEffect(() => {
    if (!isTrailing) return;

    const updateDate = () => {
      const now = new Date();
      const from = new Date(now.getTime() - defaultLookBackTime * 60 * 1000);
      setDate({ from, to: now });
    };

    updateDate();
    const intervalId = setInterval(updateDate, 5000);
    return () => clearInterval(intervalId);
  }, [isTrailing, defaultLookBackTime]);

  // Update query date based on trailing mode
  useEffect(() => {
    if (isTrailing) {
      // In trailing mode, only update if we're not currently fetching
      if (!isFetching) {
        setQueryDate(date);
      }
    } else {
      // In normal mode, update immediately
      setQueryDate(date);
    }
  }, [date, isTrailing, isFetching]);

  // Update events state
  useEffect(() => {
    // When trailing is enabled, we don't want to set the events empty on loading
    // as it is disruptive for the user
    if (isTrailing && isLoading) {
      return;
    }
    setEvents(data?.events || []);
  }, [data, isLoading, isTrailing]);

  // Update grounding events state
  useEffect(() => {
    if (isTrailing && groundingIsLoading) {
      return;
    }
    setGroundingEvents(groundingEventsAndConfigurations || []);
  }, [groundingEventsAndConfigurations, groundingIsLoading, isTrailing]);

  // Update S2S events state
  useEffect(() => {
    if (isTrailing && s2sIsLoading) {
      return;
    }
    setS2sEvents(s2sEventsAndConfigurations || []);
  }, [s2sEventsAndConfigurations, s2sIsLoading, isTrailing]);

  // Listen for date range changes from popup
  usePopupMessages(
    useCallback((msg: PopupMessage) => {
      if (msg.type !== MessageType.SET_EVENT_DATE_RANGE) return;

      const payload = msg.payload as {
        from: string;
        to: string;
        isTrailing: boolean;
      };
      const fromDate = payload.from ? new Date(payload.from) : undefined;
      const toDate = payload.to ? new Date(payload.to) : undefined;
      // Guard against invalid dates from popup messages
      const newDate: DateRange = {
        from: fromDate && !isNaN(fromDate.getTime()) ? fromDate : undefined,
        to: toDate && !isNaN(toDate.getTime()) ? toDate : undefined,
      };
      setDate(newDate);
      setIsTrailing(payload.isTrailing);
    }, []),
    [],
  );

  // Push updates to popup when events change
  useEffect(() => {
    // Only push if there's an active events popup
    if (!isPopupTypeActive(PopupType.Events)) {
      return;
    }

    const currentEvents = data?.events || [];
    const activeEventsCount = currentEvents.filter(
      (e) => !e.deactivated,
    ).length;
    const deactivatedEventsCount = currentEvents.filter(
      (e) => e.deactivated,
    ).length;

    // Generate dataKey based on event content, not date range
    // This prevents unnecessary updates when trailing mode shifts the window but events are the same.
    // isTrailing is included so a historical <-> tailing switch always propagates to the popup,
    // even when the resulting event set has an identical fingerprint.
    // Use first and last event IDs + count as a fingerprint
    const firstEventId = currentEvents[0]?.id || '';
    const lastEventId = currentEvents[currentEvents.length - 1]?.id || '';
    const dataKey = `${isTrailing ? 't' : 'h'}_${currentEvents.length}_${activeEventsCount}_${firstEventId}_${lastEventId}`;

    const fromDate = queryDate?.from;
    const toDate = queryDate?.to;
    const fromStr =
      fromDate && !isNaN(fromDate.getTime()) ? fromDate.toISOString() : '';
    const toStr =
      toDate && !isNaN(toDate.getTime()) ? toDate.toISOString() : '';

    // Send update to popup
    MainWindowActions.sendDataUpdate(DataType.EVENTS, {
      events: currentEvents,
      totalEvents: currentEvents.length,
      activeEvents: activeEventsCount,
      deactivatedEvents: deactivatedEventsCount,
      dateRange: {
        from: fromStr,
        to: toStr,
      },
      isTrailing,
      settings: {
        tableDensity:
          settings['ribbonPlugin.settings']?.table_density || 'compact',
        showEventGraph:
          settings['ribbonPlugin.settings']?.show_event_graph ?? true,
      },
      dataKey,
    });
  }, [data?.events, queryDate, settings, isTrailing]);

  const value = useMemo(
    () => ({
      events,
      groundingEvents,
      s2sEvents,
      isLoading: isLoading || groundingIsLoading || s2sIsLoading,
      date,
      setDate,
      isTrailing,
      setIsTrailing,
      defaultLookBackTime,
    }),
    [
      events,
      groundingEvents,
      s2sEvents,
      isLoading,
      groundingIsLoading,
      s2sIsLoading,
      date,
      isTrailing,
      defaultLookBackTime,
    ],
  );

  return (
    <EventsContext.Provider value={value}>{children}</EventsContext.Provider>
  );
};

export const useEventsContext = (): EventsContextValue => {
  const context = useContext(EventsContext);
  if (!context) {
    throw new Error('useEventsContext must be used within an EventsProvider');
  }
  return context;
};
